import React, { useCallback, useEffect, useState } from 'react';
import { businessAPI } from '../services/api';
import {
  buildDeclarationPayload, declarationStatusLabel, declarationToForm, emptyTaxDeclaration,
} from './businessTaxDeclarationUi';
import './BusinessTaxDeclarations.css';

const requestError = (error, fallback) => error?.response?.data?.error || error?.message || fallback;

export default function BusinessTaxDeclarations({
  tenantSlug,
  businessUid,
  mode = 'admin',
  canManage = false,
  canApprove = false,
  onNotice,
}) {
  const selfService = mode === 'self';
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyTaxDeclaration());
  const [editing, setEditing] = useState(null);
  const [reasons, setReasons] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  const [showCreateForm, setShowCreateForm] = useState(false);

  const notice = useCallback((type, text) => {
    setError(type === 'error' ? text : '');
    if (onNotice) onNotice({ type, text });
  }, [onNotice]);

  const load = useCallback(async () => {
    if (!tenantSlug || !businessUid) return;
    setLoading(true); setError('');
    try {
      const response = selfService
        ? await businessAPI.getMyTaxDeclarations(tenantSlug, businessUid)
        : await businessAPI.getTaxDeclarations(tenantSlug, businessUid);
      setRows(response.data.declarations || []);
    } catch (fetchError) {
      notice('error', requestError(fetchError, 'व्यवसाय कर घोषणा लोड करता आली नाही.'));
    } finally { setLoading(false); }
  }, [businessUid, notice, selfService, tenantSlug]);

  useEffect(() => {
    setForm(emptyTaxDeclaration()); setEditing(null); setReasons({}); setShowCreateForm(false);
    load();
  }, [load]);

  const change = event => {
    const { name, value, checked, type } = event.target;
    setForm(current => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const save = async event => {
    event.preventDefault();
    setError('');
    try {
      const payload = buildDeclarationPayload(form, {
        selfService,
        includeFinancialYear: selfService || !editing,
      });
      setSaving(editing ? `edit-${editing.declaration_uid}` : 'new');
      if (selfService) {
        await businessAPI.submitMyTaxDeclaration(tenantSlug, businessUid, payload);
        notice('success', 'आर्थिक वर्षाची कर घोषणा ग्रामपंचायत तपासणीसाठी सादर झाली.');
      } else if (editing) {
        await businessAPI.updateTaxDeclaration(tenantSlug, businessUid, editing.declaration_uid, {
          version: editing.version, ...payload,
        });
        notice('success', 'कर घोषणेचा मसुदा अद्ययावत झाला.');
      } else {
        await businessAPI.createTaxDeclaration(tenantSlug, businessUid, payload);
        notice('success', 'कर घोषणेचा मसुदा तयार झाला.');
      }
      setForm(emptyTaxDeclaration()); setEditing(null); setShowCreateForm(false);
      await load();
    } catch (saveError) {
      notice('error', requestError(saveError, 'कर घोषणा जतन करता आली नाही.'));
    } finally { setSaving(''); }
  };

  const transition = async (row, command) => {
    const reason = String(reasons[row.declaration_uid] || '').trim();
    const pastTense = { submit: 'सादर', approve: 'मंजूर', reject: 'नाकारली' }[command];
    if (command === 'reject' && reason.length < 3) return notice('error', 'नाकारण्याचे किमान ३ अक्षरी कारण भरा.');
    setSaving(`${command}-${row.declaration_uid}`); setError('');
    try {
      const fn = {
        submit: businessAPI.submitTaxDeclaration,
        approve: businessAPI.approveTaxDeclaration,
        reject: businessAPI.rejectTaxDeclaration,
      }[command];
      await fn(tenantSlug, businessUid, row.declaration_uid, { version: row.version, reason: reason || null });
      notice('success', `घोषणा ${pastTense} झाली.`);
      await load();
    } catch (transitionError) {
      notice('error', requestError(transitionError, `घोषणेवर '${pastTense}' ही कृती करता आली नाही.`));
    } finally { setSaving(''); }
  };

  const startEdit = row => {
    setEditing(row);
    setShowCreateForm(true);
    setForm(declarationToForm(row));
    setError('');
  };

  return <section className="business-tax-declarations" aria-labelledby={`business-tax-declaration-${mode}`}>
    <header className="business-tax-heading">
      <div>
        <span>आर्थिक वर्षनिहाय करपात्र माहिती</span>
        <h3 id={`business-tax-declaration-${mode}`}>व्यवसाय कर घोषणा (Tax Declarations)</h3>
        <p>उलाढाल, कर्मचारी, वर्गीकरण आणि परवाना माहिती. कर इंजिन या माहितीवरून वार्षिक व्यावसायिक कर आकारते.</p>
      </div>
      <button type="button" onClick={load} disabled={loading || Boolean(saving)}>माहिती अद्ययावत करा</button>
    </header>
    {error && <div className="business-tax-message" role="alert">{error}</div>}
    {loading ? <p className="business-tax-empty">घोषणांचा इतिहास लोड होत आहे…</p> : <div className="business-tax-history">
      {rows.map(row => <article key={row.declaration_uid} className={`business-tax-row ${row.status}`}>
        <header>
          <div>
            <strong>आर्थिक वर्ष: {row.financial_year} · आवृत्ती {row.declaration_version}</strong>
            <small>{row.declaration_uid}</small>
          </div>
          <span style={{
            background: row.status === 'approved' ? '#dcfce7' : row.status === 'submitted' ? '#fef3c7' : '#f1f5f9',
            color: row.status === 'approved' ? '#15803d' : row.status === 'submitted' ? '#b45309' : '#475569',
            fontWeight: 700,
          }}>
            {declarationStatusLabel(row.status)}
          </span>
        </header>
        <dl>
          <div><dt>वार्षिक उलाढाल</dt><dd style={{ color: '#059669', fontSize: '15px' }}>₹ {Number(row.annual_turnover || 0).toLocaleString('mr-IN')}</dd></div>
          <div><dt>कर्मचारी संख्या</dt><dd>{row.employee_count}</dd></div>
          <div><dt>वर्गीकरण</dt><dd>{row.classification_code || '—'}</dd></div>
          <div><dt>परवाना तपशील</dt><dd>{[row.license_type, row.license_number].filter(Boolean).join(' · ') || 'लागू नाही / नोंद नाही'}</dd></div>
        </dl>
        {row.decision_reason && <p className="business-tax-decision">निर्णय / शेरा: {row.decision_reason}</p>}
        {!selfService && (row.status === 'draft' || row.status === 'submitted') && <div className="business-tax-review">
          <label><span>{row.status === 'submitted' ? 'तपासणी / नकाराचे कारण' : 'सादरीकरण टीप'}</span><input value={reasons[row.declaration_uid] || ''} onChange={event => setReasons(current => ({ ...current, [row.declaration_uid]: event.target.value }))} placeholder="शेरा टाका" /></label>
          <div>
            {row.status === 'draft' && canManage && (
              <>
                <button type="button" onClick={() => startEdit(row)} disabled={Boolean(saving)}>मसुदा संपादित करा</button>
                <button type="button" onClick={() => transition(row, 'submit')} disabled={Boolean(saving)}>तपासणीसाठी सादर करा</button>
              </>
            )}
            {row.status === 'submitted' && canApprove && (
              <>
                <button type="button" className="approve" onClick={() => transition(row, 'approve')} disabled={Boolean(saving)}>✓ घोषणा मंजूर करा</button>
                <button type="button" className="reject" onClick={() => transition(row, 'reject')} disabled={Boolean(saving)}>नाकारा</button>
              </>
            )}
          </div>
        </div>}
      </article>)}
      {!rows.length && (
        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px dashed #cbd5e1', textAlign: 'center' }}>
          <p style={{ color: '#64748b', margin: '0 0 10px 0' }}>आर्थिक वर्षाची कोणतीही स्वतंत्र घोषणा अद्याप नोंदलेली नाही.</p>
          {(selfService || canManage) && !showCreateForm && (
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              style={{ padding: '8px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}
            >
              + कर घोषणेचा मसुदा तयार करा
            </button>
          )}
        </div>
      )}
    </div>}

    {(selfService || canManage) && rows.length > 0 && !editing && (
      <div style={{ marginTop: '14px' }}>
        <button
          type="button"
          onClick={() => { setShowCreateForm(prev => !prev); setForm(emptyTaxDeclaration()); }}
          style={{
            padding: '8px 16px',
            background: showCreateForm ? '#f1f5f9' : '#eff6ff',
            color: '#1d4ed8',
            border: '1px solid #bfdbfe',
            borderRadius: '6px',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          {showCreateForm ? '▲ घोषणा फॉर्म लपवा' : '+ नवीन आर्थिक वर्ष किंवा सुधारित घोषणा जोडा'}
        </button>
      </div>
    )}

    {(selfService || canManage) && (showCreateForm || Boolean(editing)) && <form className="business-tax-form" onSubmit={save} style={{ background: '#fafafa', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', marginTop: '14px' }}>
      <div className="business-tax-form-title">
        <h4>{selfService ? 'आर्थिक वर्षाची घोषणा सादर करा' : editing ? `मसुदा आवृत्ती ${editing.declaration_version} संपादित करा` : 'नवीन कर घोषणेचा मसुदा तयार करा'}</h4>
        <button type="button" onClick={() => { setEditing(null); setShowCreateForm(false); setForm(emptyTaxDeclaration()); }}>
          रद्द करा / बंद करा
        </button>
      </div>
      <div className="business-tax-grid">
        <label><span>आर्थिक वर्ष</span><input name="financial_year" value={form.financial_year} disabled={Boolean(editing)} placeholder="२०२६-२७" required onChange={change} /></label>
        <label><span>वार्षिक उलाढाल (₹)</span><input name="annual_turnover" value={form.annual_turnover} type="number" min="0" step="0.01" required onChange={change} placeholder="उदा. 500000" /></label>
        <label><span>कर्मचारी संख्या</span><input name="employee_count" value={form.employee_count} type="number" min="0" max="10000000" step="1" required onChange={change} /></label>
        <label><span>वर्गीकरण संकेतांक *</span><input name="classification_code" value={form.classification_code} maxLength="80" required onChange={change} placeholder="उदा. COMMERCIAL" /></label>
        <label><span>परवाना प्रकार (लागू असल्यास)</span><input name="license_type" value={form.license_type} maxLength="80" required={Boolean(form.license_number)} onChange={change} placeholder="उदा. Shop Act" /></label>
        <label><span>परवाना क्रमांक (लागू असल्यास)</span><input name="license_number" value={form.license_number} maxLength="120" required={Boolean(form.license_type)} onChange={change} placeholder="उदा. MH-12-345" /></label>
        <label className="wide"><span>पुरावा कागदपत्र क्रमांक (स्वल्पविरामाने वेगळे)</span><input name="document_ids" value={form.document_ids} placeholder="१२, १८" onChange={change} /></label>
        <label className="wide"><span>पुराव्याची टीप</span><textarea name="evidence_note" rows="2" value={form.evidence_note} onChange={change} /></label>
        <label className="wide"><span>अंतर्गत / सादरीकरण टीप</span><textarea name="notes" rows="2" value={form.notes} onChange={change} /></label>
      </div>
      {selfService && <label className="business-tax-attestation"><input name="attestation" type="checkbox" checked={form.attestation} onChange={change} /><span>या आर्थिक वर्षातील आकडे अचूक असून दिलेल्या पुराव्यांच्या आधारे त्यांची पडताळणी केली जाऊ शकते, याची मी पुष्टी करतो/करते.</span></label>}
      <button className="business-tax-submit" type="submit" disabled={Boolean(saving)}>{saving ? 'जतन होत आहे…' : selfService ? 'घोषणा तपासणीसाठी सादर करा' : editing ? 'मसुद्यातील बदल जतन करा' : 'मसुदा तयार करा'}</button>
    </form>}
  </section>;
}
